import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'

import { BASE_URL } from '@/api/api.constants'
import { Region } from '@/types'
import { AuthUser } from '@/types/auth'
import { syncBranchesFromRegions } from './filterSlice'
import { clearRegions, setRegions } from './regionSlice'
import { RootState } from './store'

// State interface
export interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  error: string | null
}

// Initialize from sessionStorage (checks if user has valid token)
const getInitialState = (): AuthState => {
  try {
    const token = sessionStorage.getItem('token')
    const regionsData = sessionStorage.getItem('regions')

    if (token && regionsData) {
      // User was previously authenticated
      return {
        isAuthenticated: true,
        isLoading: false,
        user: { token },
        error: null,
      }
    }
  } catch (error) {
    console.warn('Failed to initialize auth from sessionStorage:', error)
  }

  // Default unauthenticated state
  return {
    isAuthenticated: false,
    isLoading: false,
    user: null,
    error: null,
  }
}

// Login async thunk
export const login = createAsyncThunk<
  { token: string; regions: Region[] }, // Return type
  { username: string; password: string }, // Argument type
  { rejectValue: string }
>(
  'auth/login',
  async ({ username, password }, { dispatch, rejectWithValue }) => {
    try {
      // Dynamic import to avoid circular dependencies
      const { login: loginApi } = await import('@/api/login')
      const { token, regions } = await loginApi({ username, password })

      // Save to sessionStorage
      sessionStorage.setItem('token', token)
      sessionStorage.setItem('regions', JSON.stringify(regions))

      // Dispatch region update
      dispatch(setRegions(regions))

      dispatch(syncBranchesFromRegions(regions))

      return { token, regions }
    } catch (error) {
      // Clean up on error
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('regions')

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Login failed. Please try again.'
      return rejectWithValue(errorMessage)
    }
  }
)

// Logout async thunk
export const logout = createAsyncThunk<
  void, // Return type
  void, // Argument type
  { rejectValue: string }
>('auth/logout', async (_, { dispatch }) => {
  try {
    // Attempt to call logout API
    await axios.post(`${BASE_URL}/logout/`, {})
  } catch (error) {
    console.warn('Logout API call failed:', error)
  } finally {
    // Always clean up local state regardless of API success
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('regions')

    // Clear regions from Redux
    dispatch(clearRegions())
  }
})

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    // Clear authentication error
    clearAuthError: (state) => {
      state.error = null
    },

    // Set loading state manually (if needed)
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Login - loading
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      // Login - success
      .addCase(login.fulfilled, (state, action) => {
        state.isAuthenticated = true
        state.isLoading = false
        state.user = { token: action.payload.token }
        state.error = null
      })
      // Login - error
      .addCase(login.rejected, (state, action) => {
        state.isAuthenticated = false
        state.isLoading = false
        state.user = null
        state.error = action.payload || 'Login failed'
      })

      // Logout - loading
      .addCase(logout.pending, (state) => {
        state.isLoading = true
      })
      // Logout - success
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false
        state.isLoading = false
        state.user = null
        state.error = null
      })
      // Logout - error
      .addCase(logout.rejected, (state) => {
        state.isAuthenticated = false
        state.isLoading = false
        state.user = null
        state.error = null
      })
  },
})

// Export actions
export const { clearAuthError, setAuthLoading } = authSlice.actions

// Export reducer
export const authReducer = authSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated
export const selectAuthLoading = (state: RootState) => state.auth.isLoading
export const selectAuthError = (state: RootState) => state.auth.error
